import { useEffect, useRef, useState } from 'react'
import { Eye, Lock, Mic, MicOff, ShieldBan, ShieldCheck, Video, VideoOff, type LucideIcon } from 'lucide-react'
import { Button, IconButton, Notice, Select, Skeleton, SkeletonGroup, type DialogProps } from '@/components/play'
import { inviteError } from '@/lib/watch/invitesApi'
import { formatAudience } from '@/lib/watch/rules'
import {
  MIC_LEVEL_BARS,
  useCameraCheck,
  useMicLevel,
  type CameraCheck,
  type CameraCheckDevice,
  type CameraCheckProblem,
} from '@/lib/watch/useCameraCheck'
import { useMediaQuery } from '@/lib/watch/useMediaQuery'
import { StagePresenceFailure } from '@/lib/watch/useStagePresence'
import { cn } from '@/lib/utils'
import { WatchSheet } from './WatchSheet'
import './watch.css'
import './invites.css'

/** What the person chose in the check, for the page to go live with. */
export interface CameraCheckChoice {
  /** The camera to publish, or null to join with the microphone only (there is no camera). */
  videoDeviceId: string | null
  audioDeviceId: string | null
  /** Join with the camera on. Always false without a camera. */
  cameraOn: boolean
  micOn: boolean
}

export interface CameraCheckSheetProps {
  open: boolean
  /** How many others are watching: they will see and hear the person. */
  audienceCount: number
  /**
   * Join stage. The check hands its camera and microphone back first, so the page can open them (some phones can't
   * open a camera twice). Return the promise to show progress: a rejection reopens the preview and says why in the
   * sheet (a full stage, say; a `StagePresenceFailure` with its own words). The page closes the sheet once the person
   * is on stage.
   */
  onJoin(choice: CameraCheckChoice): void | Promise<unknown>
  /**
   * Not now, the close button, Escape or the backdrop. Not while joining: the page may be taking the seat, and a cancel
   * racing it would leave as the seat lands (as the Play confirm dialog keeps its question until its action settles).
   */
  onCancel(): void
  /** The stage's host, for the title: "Join Shane’s stage". */
  hostName?: string | null
  /** The page is joining (when `onJoin` doesn't return its promise). */
  joining?: boolean
  /** Where focus goes when the sheet closes. */
  finalFocus?: DialogProps['finalFocus']
}

const PROBLEM_WORDS: Record<CameraCheckProblem, string> = {
  insecure: 'Your camera needs a secure connection. Open this page with https.',
  unsupported: 'This browser can’t use your camera. Try another browser.',
  denied: 'Allow them for this site from the camera icon in the address bar, or in your browser’s site settings, then try again. Your device’s settings can block the browser too.',
  missing: 'We can’t find a camera or microphone. Connect them, then try again.',
  mic_missing: 'We can’t find a microphone. Connect one, then try again.',
  busy: 'Another app is using your camera or microphone. Close it, then try again.',
  failed: 'We couldn’t start your camera or microphone. Try again.',
}

/** Only another address or browser can help these. */
const NO_RETRY = new Set<CameraCheckProblem>(['insecure', 'unsupported'])

/** What the preview shows when there is no picture, by cause. */
const NO_PICTURE: Record<CameraCheckProblem, { Icon: LucideIcon; label: string }> = {
  insecure: { Icon: Lock, label: 'Not a secure connection' },
  unsupported: { Icon: VideoOff, label: 'No camera' },
  denied: { Icon: ShieldBan, label: 'Camera and microphone blocked' },
  missing: { Icon: VideoOff, label: 'No camera or microphone' },
  mic_missing: { Icon: MicOff, label: 'No microphone' },
  busy: { Icon: VideoOff, label: 'Camera or microphone in use' },
  failed: { Icon: VideoOff, label: 'No camera' },
}

/**
 * The camera and microphone check before a guest goes on stage (docs/features/prototypes/watch-experience, the
 * backstage dialog): a mirrored local preview with mute toggles and a live microphone level, camera and microphone
 * pickers when there is a choice, and how many people will see and hear them. **Join stage** hands the choice to
 * `onJoin`; **Not now** cancels (not while joining: Escape and the backdrop wait too). Asking for permission, blocked (and how to allow it), no camera (join with the
 * microphone only) and other failures each say what happened. Every track stops when the sheet closes or unmounts. A
 * bottom sheet on phones and a dialog on the desktop (`WatchSheet`).
 */
export function CameraCheckSheet({ open, audienceCount, onJoin, onCancel, hostName, joining = false, finalFocus }: CameraCheckSheetProps) {
  const check = useCameraCheck(open)
  const joinRef = useRef<HTMLButtonElement>(null)
  const [pending, setPending] = useState(false)
  /** `onJoin`'s promise resolved: the person is on stage, and the devices stay with the page until the sheet closes. */
  const [joined, setJoined] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    if (!open) {
      setJoinError(null)
      setPending(false)
      setJoined(false)
    }
  }, [open])

  // Handed back for a join that didn't happen (it failed, or the page started nothing): bring the preview back.
  const { phase, retry } = check
  useEffect(() => {
    if (open && phase === 'released' && !pending && !joining && !joined) retry()
  }, [open, phase, pending, joining, joined, retry])

  const busy = pending || joining
  const canJoin = check.phase === 'ready' && !busy

  const join = async () => {
    if (!canJoin) return
    setJoinError(null)
    const choice: CameraCheckChoice = {
      videoDeviceId: check.hasCamera ? check.videoDeviceId : null,
      audioDeviceId: check.audioDeviceId,
      cameraOn: check.hasCamera && check.cameraOn,
      micOn: check.micOn,
    }
    setPending(true)
    check.release()
    let result: void | Promise<unknown>
    try {
      result = onJoin(choice)
    } catch (error) {
      result = Promise.reject(error)
    }
    if (!result || typeof (result as Promise<unknown>).then !== 'function') {
      setPending(false)
      return
    }
    try {
      await result
      setJoined(true)
    } catch (error) {
      if (openRef.current) setJoinError(joinFailure(error))
    } finally {
      setPending(false)
    }
  }

  return (
    <WatchSheet
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onCancel()
      }}
      title={hostName ? `Join ${hostName}’s stage` : 'Join the stage'}
      initialFocus={joinRef}
      finalFocus={finalFocus}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Not now
          </Button>
          <Button ref={joinRef} focusableWhenDisabled disabled={!canJoin && !busy} loading={busy} onClick={() => void join()}>
            <Mic aria-hidden="true" />
            Join stage
          </Button>
        </>
      }
    >
      <CheckBody check={check} audienceCount={audienceCount} joinError={joinError} />
    </WatchSheet>
  )
}

function CheckBody({ check, audienceCount, joinError }: { check: CameraCheck; audienceCount: number; joinError: string | null }) {
  const { phase, problem, stream, hasCamera, cameraOn, micOn, cameras, microphones } = check
  const ready = phase === 'ready' && stream !== null
  const noPicture = problem ? NO_PICTURE[problem] : { Icon: VideoOff, label: hasCamera ? 'Your camera is off' : 'No camera' }

  return (
    <div className="watch-check">
      <div className="watch-check__preview">
        {phase === 'opening' || phase === 'idle' || phase === 'released' ? (
          <SkeletonGroup label={phase === 'released' ? 'Joining the stage' : 'Starting your camera'} className="watch-check__fill">
            <Skeleton shape="media" className="watch-check__fill" />
          </SkeletonGroup>
        ) : ready && hasCamera && cameraOn ? (
          <div role="img" aria-label="Your camera" className="watch-check__fill">
            <PreviewVideo stream={stream} mirrored={check.facingUser} />
          </div>
        ) : (
          // No picture: the camera is off, there is none, or it didn't open (the glyph says why). Never a frozen frame.
          <div role="img" aria-label={noPicture.label} className="watch-check__fill watch-check__off">
            <noPicture.Icon aria-hidden="true" />
          </div>
        )}
        {ready && (
          <div className="watch-check__tools">
            <IconButton variant="ghost" elevated label="Mute" aria-pressed={!micOn} onClick={check.toggleMic}>
              {micOn ? <Mic /> : <MicOff />}
            </IconButton>
            <MicLevel stream={stream} micOn={micOn} />
            {hasCamera && (
              <IconButton variant="ghost" elevated label="Turn camera off" aria-pressed={!cameraOn} onClick={check.toggleCamera}>
                {cameraOn ? <Video /> : <VideoOff />}
              </IconButton>
            )}
          </div>
        )}
      </div>

      {phase === 'opening' && check.asking && (
        <p className="watch-camera-check__hint">
          <ShieldCheck aria-hidden="true" />
          Allow your camera and microphone when your browser asks.
        </p>
      )}

      {ready && (cameras.length > 1 || microphones.length > 1) && (
        <div className="watch-check__devices">
          {hasCamera && cameras.length > 1 && (
            <Select
              label="Camera"
              hideLabel
              icon={<Video />}
              items={deviceItems(cameras)}
              value={check.videoDeviceId}
              onValueChange={(deviceId) => deviceId && check.selectCamera(deviceId)}
            />
          )}
          {microphones.length > 1 && (
            <Select
              label="Microphone"
              hideLabel
              icon={<Mic />}
              items={deviceItems(microphones)}
              value={check.audioDeviceId}
              onValueChange={(deviceId) => deviceId && check.selectMicrophone(deviceId)}
            />
          )}
        </div>
      )}

      <p className="watch-check__audience">
        <Eye aria-hidden="true" />
        {audienceWords(audienceCount)}
      </p>

      {ready && !hasCamera && <Notice>We can’t find a camera. You can join with your microphone only.</Notice>}

      {phase === 'failed' && problem && (
        <Notice
          tone="error"
          alert
          title={problem === 'denied' ? 'Camera and microphone blocked' : undefined}
          action={
            NO_RETRY.has(problem) ? undefined : (
              <Button variant="secondary" size="compact" onClick={check.retry}>
                Try again
              </Button>
            )
          }
        >
          {PROBLEM_WORDS[problem]}
        </Notice>
      )}

      {joinError && (
        <Notice tone="error" alert>
          {joinError}
        </Notice>
      )}
    </div>
  )
}

/** The microphone's level as six rising bars in a pill beside the mute toggle. A reading, so it isn't announced. */
function MicLevel({ stream, micOn }: { stream: MediaStream; micOn: boolean }) {
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const level = useMicLevel(stream, micOn, reduced)
  return (
    <span
      className="watch-camera-check__level"
      role="meter"
      aria-label="Microphone level"
      aria-valuemin={0}
      aria-valuemax={MIC_LEVEL_BARS}
      aria-valuenow={level}
      aria-valuetext={micOn ? (level > 0 ? 'Picking you up' : 'Quiet') : 'Muted'}
      data-muted={!micOn || undefined}
    >
      {Array.from({ length: MIC_LEVEL_BARS }, (_, bar) => (
        <span key={bar} className="watch-camera-check__bar" data-on={bar < level || undefined} />
      ))}
    </span>
  )
}

/** The local camera, muted (the microphone is never played back), mirrored like a face unless it faces away. */
function PreviewVideo({ stream, mirrored }: { stream: MediaStream; mirrored: boolean }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.srcObject = stream
    return () => {
      element.srcObject = null
    }
  }, [stream])
  return (
    <video
      ref={ref}
      className={cn('watch-check__video', mirrored && 'watch-check__video--mirrored')}
      muted
      playsInline
      autoPlay
      disablePictureInPicture
    />
  )
}

const deviceItems = (devices: CameraCheckDevice[]) => devices.map((device) => ({ value: device.deviceId, label: device.label }))

/** Who will see and hear the person: "No one’s watching yet", "1 person …", "1.2K people …". */
export function audienceWords(count: number): string {
  if (!(count > 0)) return 'No one’s watching yet'
  if (count === 1) return '1 person will see and hear you'
  return `${formatAudience(count)} people will see and hear you`
}

/**
 * Why joining failed, for the sheet: the words a presence refusal comes with (`useStagePresence`'s `join`, in a guest's
 * words), else read as an invite call's failure.
 */
function joinFailure(error: unknown): string {
  if (error instanceof StagePresenceFailure) return error.message
  const failure = inviteError(error)
  switch (failure.type) {
    case 'stage_full':
      return 'The stage is full now. Try again when a seat frees up.'
    case 'forbidden':
      return 'Your invitation is no longer open. Ask the host to invite you again.'
    case 'network':
    case 'rate_limited':
      return failure.message
    default:
      return 'We couldn’t put you on stage. Try again.'
  }
}
